import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Modal,
  useWindowDimensions,
  TextInput,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  CommentSection,
  CommentData,
  getAvatarColorFromUserType,
  UserType,
  KPIDistributionCard,
  EvidenceTable,
} from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { unitGoalsApi, unitGoalCommentsApi, UnitGoalComment as ApiUnitGoalComment } from '../../services/apiService';
import { UnitGoal, KPIList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'UnitGoalDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MoreVerticalIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="2" fill={color} />
    <Circle cx="12" cy="12" r="2" fill={color} />
    <Circle cx="12" cy="19" r="2" fill={color} />
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Component
// ============================================================

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

const CircularProgress = ({ percentage, size = 168, strokeWidth = 12 }: CircularProgressProps) => {
  const { isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(100, Math.max(0, percentage));
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = commitmentColor(percentage);
  const bgColor = isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"];

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((progress / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={bgColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {progress > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={styles.progressTextContainer}>
        <Text style={[styles.progressPercentage, { color: isDark ? tokens.colors.raw.gray["50"] : tokens.colors.semantic.general.foreground }]}>%{formatPct(percentage)}</Text>
      </View>
    </View>
  );
};

// ============================================================
// Main Screen
// (KPI cards use the shared <TargetAchievementCard/> — same component
//  as Process/Service detail screens.)
// ============================================================

export default function UnitGoalDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { width } = useWindowDimensions();
  const { horizontalCardWidth, isDesktop } = useResponsive();
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, canComment, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateUnit } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"],
    sectionBg: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unitGoal, setUnitGoal] = useState<UnitGoal | null>(null);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // Comments state
  const [apiComments, setApiComments] = useState<ApiUnitGoalComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);

  // Unified with useResponsive — stack on tablet + mobile (< 1024px).
  const isSmallScreen = !isDesktop;

  // Convert API comment to display format (unified with ServiceDetailScreen pattern)
  const convertApiComment = (apiComment: ApiUnitGoalComment): CommentData => {
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ');
    const firstInitial = nameParts[0]?.[0] || '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase() || 'U';

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;

    return {
      id: apiComment.id,
      author: fullName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor: getAvatarColorFromUserType(userType),
      showMenu: isOwner,
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  // Fetch unit goal comments
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await unitGoalCommentsApi.getForUnitGoal(id);
      setApiComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch the unit goal (KPIs included in response)
      const goalData = await unitGoalsApi.get(id);
      setUnitGoal(goalData);

      // Use KPIs from main response instead of separate API call
      setKpis(goalData.kpis || []);

      // Fetch comments for this unit goal
      fetchComments();
    } catch (error) {
      console.error('Error fetching unit goal:', error);
      showError(t('employees.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, showError, fetchComments]);

  // Close menu on outside click/scroll
  useEffect(() => {
    if (!showMenu) return;
    const handleClose = () => setShowMenu(false);
    document.addEventListener('mousedown', handleClose);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [showMenu]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleEdit = () => {
    setShowMenu(false);
    navigation.navigate('AddUnitGoal', { unitGoalId: id });
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await unitGoalsApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('unitGoals.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('unitGoals.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  // Comment handlers - unified with ServiceDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      await unitGoalCommentsApi.create({
        unit_goal: id,
        content: text,
        comment_type: 'note',
      });
      showSuccess(t('comments.addSuccess'));
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      showError(t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (!editingComment) return;
    try {
      await unitGoalCommentsApi.update(editingComment.id, { content: text });
      showSuccess(t('comments.updateSuccess'));
      setEditingComment(null);
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      showError(t('comments.updateFailed'));
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await unitGoalCommentsApi.delete(commentId);
      showSuccess(t('comments.deleteSuccess'));
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      showError(t('comments.deleteFailed'));
    }
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  const handleCommentCancel = () => {
    setEditingComment(null);
    setShowAddComment(false);
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = unitGoal?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0, total: 0 };
  const totalKpis = (distribution.total ?? kpis.length) || 1;
  const excellentPercent = distribution.excellent ?? 0;
  const goodPercent = distribution.good ?? 0;
  const needsImprovementPercent = distribution.needs_improvement ?? 0;

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!unitGoal) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>{t('goals.notFound')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, isSmallScreen && { padding: tokens.spacing.absolute["4"] }]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('unitGoals.title'), route: 'UnitGoals' },
            { label: t('unitGoals.unitGoalDetails') },
          ]}
        />

        <PageHeader
          title={translate(unitGoal.name)}
          description={unitGoal.description ? translate(unitGoal.description) : undefined}
          actions={
            !isViewOnly ? (
              <View style={styles.headerActions}>
                <View ref={menuButtonRef}>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    icon={<MoreVerticalIcon color={themeColors.textMuted} />}
                    accessibilityLabel={t('common.menu')}
                    onPress={() => {
                      if (!showMenu) {
                        const node = menuButtonRef.current as any;
                        if (node && node.getBoundingClientRect) {
                          const rect = node.getBoundingClientRect();
                          setMenuPosition({ top: rect.bottom + 4, left: rect.left });
                        }
                      }
                      setShowMenu(!showMenu);
                    }}
                  />
                </View>

                {showMenu && ReactDOM.createPortal(
                  <View
                    // @ts-ignore
                    onMouseDown={(e: any) => e.stopPropagation()}
                    style={[styles.menuDropdown, {
                      position: 'fixed' as any,
                      top: menuPosition.top,
                      start: menuPosition.left,
                      backgroundColor: themeColors.card,
                      borderColor: themeColors.cardBorder,
                    }]}
                  >
                    <TouchableOpacity
                      style={[styles.menuItem, { borderBottomColor: themeColors.border }]}
                      onPress={handleEdit}
                    >
                      <Text style={[styles.menuItemText, { color: themeColors.textPrimary }]}>{t('common.edit')}</Text>
                      <EditIcon size={16} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.menuItem, { borderBottomWidth: 0 }]}
                      onPress={() => {
                        setShowMenu(false);
                        handleDelete();
                      }}
                    >
                      <Text style={[styles.menuItemText, { color: tokens.colors.raw.red["500"] }]}>
                        {t('common.delete')}
                      </Text>
                      <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                    </TouchableOpacity>
                  </View>,
                  document.body
                )}
              </View>
            ) : undefined
          }
        />

        {/* Pending Deletion Banner */}
        {(unitGoal as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(unitGoal as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (unitGoal as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => {
              showSuccess(t('deletion.approved'));
              navigation.goBack();
            }}
            onRejected={() => {
              showSuccess(t('deletion.rejected'));
              fetchData();
            }}
            onCancelled={() => {
              showSuccess(t('deletion.requestCancelled'));
              fetchData();
            }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Info Cards Row - RTL order: Basic Info (right), KPIs (middle), Distribution (left) */}
        <View style={[styles.infoCardsRow, isSmallScreen && styles.infoCardsRowMobile]}>
          {/* KPI Status Distribution Card - appears on LEFT in RTL */}
          <View style={[styles.infoCard, styles.infoCardDistribution, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.infoCardMobile]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.distributionContent}>
              {/* Donut Chart placeholder - left side */}
              <View style={styles.donutChartContainer}>
                <Svg width={140} height={140} viewBox="0 0 140 140">
                  {/* Background circle */}
                  <Circle cx="70" cy="70" r="50" fill="transparent" stroke={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]} strokeWidth="20" />
                  {/* Green segment - Excellent */}
                  {excellentPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#2CA02C"
                      strokeWidth="20"
                      strokeDasharray={`${excellentPercent * 3.14} ${314 - excellentPercent * 3.14}`}
                      strokeDashoffset="78.5"
                      transform="rotate(-90 70 70)"
                    />
                  )}
                  {/* Orange segment - Good */}
                  {goodPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#FF7F0E"
                      strokeWidth="20"
                      strokeDasharray={`${goodPercent * 3.14} ${314 - goodPercent * 3.14}`}
                      strokeDashoffset={78.5 - excellentPercent * 3.14}
                      transform="rotate(-90 70 70)"
                    />
                  )}
                  {/* Red segment - Needs Improvement */}
                  {needsImprovementPercent > 0 && (
                    <Circle
                      cx="70"
                      cy="70"
                      r="50"
                      fill="transparent"
                      stroke="#D62728"
                      strokeWidth="20"
                      strokeDasharray={`${needsImprovementPercent * 3.14} ${314 - needsImprovementPercent * 3.14}`}
                      strokeDashoffset={78.5 - (excellentPercent + goodPercent) * 3.14}
                      transform="rotate(-90 70 70)"
                    />
                  )}
                </Svg>
              </View>
              {/* Legend - right side */}
              <View style={styles.statusList}>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {excellentPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('dashboard.excellent')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#2CA02C' }]} />
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {goodPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('goals.good')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#FF7F0E' }]} />
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={[styles.statusPercent, { color: themeColors.textPrimary }]}>% {needsImprovementPercent}</Text>
                  <View style={styles.statusLabelContainer}>
                    <Text style={[styles.statusLabel, { color: themeColors.textMuted }]}>{t('goals.needsImprovement')}</Text>
                    <View style={[styles.statusBar, { backgroundColor: '#D62728' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* KPIs Progress Card - appears in MIDDLE in RTL */}
          <View style={[styles.infoCard, styles.infoCardKpi, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && [styles.infoCardMobile, styles.infoCardKpiMobile]]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
            <View style={styles.kpiProgressContainer}>
              {unitGoal.commitment_rate != null ? (
                <CircularProgress percentage={unitGoal.commitment_rate} />
              ) : (
                <View style={{ justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>

          {/* Basic Information Card - appears on RIGHT in RTL */}
          <View style={[styles.infoCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.infoCardMobile]}>
            <Text style={[styles.infoCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.basicInformation')}</Text>
            <View style={styles.infoItemsContainer}>
              <View style={[styles.infoItem, { backgroundColor: themeColors.sectionBg }]}>
                <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('unitGoals.organizationalUnit')}</Text>
                <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{translateUnit(unitGoal.unit_name)}</Text>
              </View>
              {unitGoal.strategic_goal_title && (
                <View style={[styles.infoItem, { backgroundColor: themeColors.sectionBg }]}>
                  <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('unitGoals.linkedStrategicGoal')}</Text>
                  <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{translate(unitGoal.strategic_goal_title)}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bottom Section - RTL: Comments (left), Goal Linkages (right) */}
        <View style={[styles.bottomSection, isSmallScreen && styles.bottomSectionMobile]}>
          {/* Comments Section - appears on LEFT in RTL - Unified with ServiceDetailScreen */}
          <View style={[styles.commentsColumn, isSmallScreen && styles.commentsColumnSmall]}>
            <CommentSection
              loading={commentsLoading}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCommentCancel}
            />
          </View>

          {/* Goal Linkages - appears on RIGHT in RTL (takes 2 columns) */}
          <View style={[styles.linkagesCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.linkagesCardMobile]}>
            <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('unitGoals.linkedKPIs')}</Text>

            {/* KPIs Section with gray background */}
            <View style={[styles.kpisSection, { backgroundColor: themeColors.sectionBg }]}>
              <View style={styles.kpisSectionHeader}>
                {/* Add button on LEFT in RTL - hidden for view-only users */}
                {!isViewOnly && (
                  <TouchableOpacity
                    style={[styles.addKpiButton, { backgroundColor: themeColors.card, borderColor: themeColors.border }]}
                    onPress={() => navigation.navigate('AddUnitGoal', {
                      unitGoalId: id,
                      scrollToKpi: true,
                    })}
                  >
                    <Text style={[styles.addKpiButtonText, { color: themeColors.textPrimary }]}>{t('kpis.addIndicator')}</Text>
                    <PlusIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                {/* Title on RIGHT in RTL */}
                <Text style={[styles.kpisSectionTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
              </View>

              {/* KPI Cards - Horizontal Scroll with RTL transform */}
              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.horizontalCardsRow}
                  style={styles.horizontalScrollContainer}
                >
                  {kpis.length === 0 ? (
                    <View style={[styles.emptyKpis, styles.rtlCard]}>
                      <Text style={styles.emptyKpisText}>{t('kpis.noKPIs')}</Text>
                    </View>
                  ) : (
                    kpis.map((kpi) => {
                      const achievement = parseFloat(String(kpi.achievement_percentage ?? '0')) || 0;
                      // Color based on achievement: green >= 80%, yellow >= 50%, red < 50%
                      // (same thresholds as Process/Service detail KPI cards)
                      const progressColor =
                        achievement >= 80
                          ? '#2CA02C'
                          : achievement >= 50
                            ? tokens.colors.raw.yellow["500"]
                            : tokens.colors.raw.red["500"];

                      return (
                        <View key={kpi.id} style={[styles.rtlCard, { width: horizontalCardWidth }]}>
                          <TargetAchievementCard
                            title={translate(kpi.name)}
                            achieved={kpi.latest_value || '0'}
                            target={kpi.target_value}
                            unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                            achievedColor={progressColor}
                            percentage={achievement}
                            onViewDetails={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                          />
                        </View>
                      );
                    })
                  )}
                </ScrollView>
              </View>
            </View>
          </View>
        </View>
        <EvidenceTable
          relatedType="unit_goal"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'unit_goal', relatedId: id, contextLabel: t('unitGoals.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('unitGoals.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={unitGoal?.name || ''}
        fetchImpact={async () => {
          const response = await unitGoalsApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.red["500"],
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["400"],
  },
  breadcrumbActive: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.primary,
  },
  headerActions: {
    position: 'relative',
    zIndex: 9999,
  },
  backButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.gray["100"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["6"],
    borderRadius: tokens.radii.absolute["radius-8"],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  backButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontWeight: '600',
  },
  menuDropdown: {
    position: 'absolute',
    top: 44,
    start: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    minWidth: 140,
    zIndex: 99999,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  menuItemText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
  },
  infoCardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  infoCardsRowMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  infoCard: {
    flex: 1,
    minWidth: 250,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  infoCardMobile: {
    flex: 'none' as any,
    minWidth: 'auto' as any,
    width: '100%',
  },
  infoCardDistribution: {
    flex: 2,
    minWidth: 280,
  },
  infoCardKpi: {
    flex: 1,
    minWidth: 180,
    maxWidth: 300,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: tokens.spacing.absolute["5"],
    paddingVertical: tokens.spacing.absolute["5"],
  },
  infoCardKpiMobile: {
    maxWidth: '100%',
    width: '100%',
  },
  infoCardTitle: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    lineHeight: 24,
    width: '100%',
  },
  infoItemsContainer: {
    gap: tokens.spacing.absolute["6"],
  },
  infoItem: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    gap: tokens.spacing.absolute["2"],
  },
  infoLabel: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#44546A',
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  infoValue: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  progressDotContainer: {
    position: 'absolute',
    top: 0,
    start: '50%',
    marginStart: -7.5,
  },
  progressDot: {
    width: 15,
    height: 15,
    borderRadius: 7.5,
    backgroundColor: '#2CA02C',
    borderWidth: 2,
    borderColor: '#FCFCFC',
  },
  progressTextContainer: {
    position: 'absolute',
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressPercentage: {
    fontFamily: FONTS.numbers,
    fontSize: tokens.typography["heading 4"]["font size"],
    color: tokens.colors.semantic.general.foreground,
    fontWeight: '700',
  },
  distributionContent: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 40,
  },
  donutChartContainer: {
    width: 140,
    height: 140,
    marginEnd: tokens.spacing.absolute["5"],
  },
  statusList: {
    gap: tokens.spacing.absolute["6"],
    justifyContent: 'center',
  },
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  statusLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    width: 80,
  },
  statusPercent: {
    fontFamily: FONTS.numbers,
    fontSize: tokens.typography["heading 4"]["font size"],
    color: '#303030',
    fontWeight: '600',
  },
  statusLabel: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
  },
  statusBar: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  bottomSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
  },
  bottomSectionMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  linkagesCard: {
    flex: 2,
    minWidth: 300,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linkagesCardMobile: {
    flex: 'none' as any,
    minWidth: 'auto' as any,
    width: '100%',
  },
  // Comments Column - Unified with ServiceDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
  },
  sectionTitle: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 20,
  },
  kpisSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    gap: tokens.spacing.absolute["5"],
  },
  kpisSectionTitle: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  emptyKpis: {
    padding: tokens.spacing.absolute["8"],
    alignItems: 'center',
  },
  emptyKpisText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["400"],
  },
  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    fontWeight: '600',
  },
  modalMessage: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  modalDeleteButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  modalRejectButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
  },
  rejectionReasonInput: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginVertical: tokens.spacing.absolute["4"],
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    textAlignVertical: 'top',
  },
});

const styles = getStyles(true);
